import ky from 'ky';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Col, Row, Button } from 'react-bootstrap';

import BayLoader from '../../BayLoader';
import BayRcDialog from '../../BayRcDialog';
import BaySelect from '../../BaySelect';

interface SnoozeOption {
	label: string;
	value: number;
}

interface SnoozeData {
	id: number | string;
	title: string;
	[key: string]: unknown;
}

interface SnoozeModalProps {
	setArrayCount: (array: unknown[]) => void;
	notificationsArray: unknown[];
	showModal: boolean;
	onHideFunc: (type: string, success?: boolean) => void;
	data: SnoozeData;
}

interface SnoozeResponse {
	STATUS: number;
	MESSAGE: string;
}

interface Notification {
	id: number | string;
	notificationid: string;
}

// Module scope for a STABLE IDENTITY: BaySelect's seed effect holds
// `props.defaultValue` in its dependency array, so a new object per render re-ran
// it and reset the control -- picking "15 Minutes" snapped back to "2 Minutes".
const DEFAULT_OPTION: SnoozeOption = { label: '2 Minutes', value: 2 };

const SnoozeModal = ({
	setArrayCount,
	notificationsArray,
	showModal,
	onHideFunc,
	data,
}: SnoozeModalProps) => {
	// DERIVED, never a literal: this was 5 while the control showed 2 Minutes, so an
	// untouched Snooze snoozed for a duration it never displayed.
	const [selectedOption, setSelectedOption] = useState<number>(DEFAULT_OPTION.value);
	const [loading, setLoading] = useState<boolean>(false);

	// One controller PER REQUEST. Was module scope and shared by every mount, so the
	// first Cancel/close aborted it forever and later requests died silently.
	const abortRef = useRef<AbortController | null>(null);

	useEffect(() => {
		return () => {
			abortRef.current?.abort();
		};
	}, []);

	const { id, title, ...rest } = data;

	const options: SnoozeOption[] = [
		DEFAULT_OPTION,
		{ label: '5 Minutes', value: 5 },
		{ label: '15 Minutes', value: 15 },
		{ label: '30 Minutes', value: 30 },
		{ label: '45 Minutes', value: 45 },
		{ label: '1 Hour', value: 60 },
		{ label: '2 Hours', value: 120 },
		{ label: '4 Hours', value: 240 },
		{ label: '1 Day', value: 1440 },
	];

	// `.bay-float-body` is overflow-y:auto and clips on BOTH axes, so an inline menu
	// is cut off at its edge. Same portal treatment ActAsModal already carries;
	// `menuPosition: fixed` keeps it anchored. 1080 clears our raised backdrop below.
	const menuPortalTarget = typeof document !== 'undefined' ? document.body : undefined;
	const menuStyles = useMemo(
		() => ({
			// eslint-disable-next-line no-restricted-syntax -- grandfathered: raw z-index fenced as step 2b, the portal band is a cross-repo decision. See docs/css-unification/overlay-and-zindex.md
			menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 1080 }),
		}),
		[],
	);

	const hideAndCancel = () => {
		abortRef.current?.abort();
		setSelectedOption(DEFAULT_OPTION.value);
		onHideFunc('snooze');
	};

	const snoozeSave = async (dismissBool?: boolean) => {
		let newArray = [...notificationsArray];

		const formData = new FormData();
		formData.append('method', 'snooze');
		formData.append('returnformat', 'json');
		formData.append(
			'id',
			Array.isArray(rest[0])
				? (rest[0] as unknown[]).join(',')
				: id.toString(),
		);
		formData.append('minutes', dismissBool ? '0' : selectedOption.toString());

		const removeNotification = (stringId: string) => {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(window as any).globalStore.dispatch({
				type: 'notifications/removeNotification',
				payload: stringId,
			});
		};

		try {
			setLoading(true);
			abortRef.current = new AbortController();
			const res = await ky
				.post('/global/components/notifications.cfc', {
					body: formData,
					signal: abortRef.current.signal,
					retry: 2,
					timeout: 30000,
				})
				.json<SnoozeResponse>();

			if (res.STATUS === 0 && res.MESSAGE === 'Process Complete') {
				if (Array.isArray(rest[0])) {
					for (const valueId of rest[0]) {
						const index = (newArray as Notification[]).findIndex(
							(e) => e.id === valueId,
						);
						if (index > -1) {
							removeNotification((newArray[index] as Notification).notificationid);
							newArray.splice(index, 1);
						}
					}
				} else {
					removeNotification(rest[0] as string);
					newArray = (notificationsArray as Notification[]).filter(
						(e) => e.id !== id,
					);
				}

				setArrayCount(newArray);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(window as any).globalStore.dispatch(
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(window as any).globalStore.fetchNotifications(),
				);
				onHideFunc('snooze', true);
				setLoading(false);
			} else {
				setLoading(false);
			}
		} catch (error) {
			setLoading(false);
			if ((error as Error).name === 'AbortError') {
				console.log('Snooze action aborted by user');
			} else {
				console.error('Snooze action error:', error);
			}
		}
	};

	const ModalLoading = () => {
		return (
			<div style={{ display: 'flex', justifyContent: 'center' }}>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						alignItems: 'center',
						textAlign: 'center',
					}}
				>
					<BayLoader small />
					<p>Please wait...</p>
				</div>
			</div>
		);
	};

	const SnoozeModalFooter = () => {
		return (
			<div className="action-button d-flex m-0 w-100 justify-content-between">
				<Button variant="outline-secondary" onClick={() => hideAndCancel()}>
					Cancel
				</Button>
				<Button
					className="ms-3"
					disabled={loading}
					variant="primary"
					onClick={() => snoozeSave()}
				>
					{Array.isArray(rest[0]) ? 'Snooze All' : 'Snooze'}
				</Button>
			</div>
		);
	};

	return (
		<BayRcDialog
			closeButton
			buttonText="Snooze"
			className="bay-snooze-modal"
			footer={<SnoozeModalFooter />}
			mask
			show={showModal}
			title={
				Array.isArray(rest[0]) ? `Snooze All ${title} Items` : `Snooze ${title}`
			}
			// Clears the notifications drawer's raised scrim, which is full-viewport and
			// swallowed every click here. Must be the BACKDROP: it owns the stacking
			// context the panel sits in, so raising the panel alone does nothing.
			zIndexBackdrop="var(--bs-zindex-popover, 1070)"
			onHideFunc={() => hideAndCancel()}
		>
			{loading ? (
				<ModalLoading />
			) : (
				<Row className="d-flex align-items-center">
					<Col sm={5} xs={12}>
						<h4>Dismiss</h4>
						<small className="text-muted lh-lg">
							Hide notification indefinitely.
						</small>
						<div className="mt-2">
							<Button
								disabled={loading}
								variant="warning"
								onClick={() => snoozeSave(true)}
							>
								{Array.isArray(rest[0]) ? 'Dismiss All' : 'Dismiss'}
							</Button>
						</div>
					</Col>
					<Col className="d-none d-sm-flex" sm="auto">
						<h5>-Or-</h5>
					</Col>
					<Col className="mt-3 mt-sm-0" sm={5} xs={12}>
						<hr className="d-flex d-sm-none" />
						<h4>Set Timer</h4>
						<small className="text-muted lh-lg">Snooze timer amount:</small>
						<div className="mt-2">
							<BaySelect
								flex
								customStyles={menuStyles}
								defaultValue={DEFAULT_OPTION}
								isClearable={false}
								menuPortalTarget={menuPortalTarget}
								menuPosition="fixed"
								optionsArray={options}
								onChangeFunc={(e: SnoozeOption) => setSelectedOption(e.value)}
							/>
						</div>
					</Col>
				</Row>
			)}
		</BayRcDialog>
	);
};

export default SnoozeModal;
